import { useContext, useRef, useState } from "react"
import { useT } from "react-polyglot-hooks"
import * as PropTypes from "prop-types"
import styled from "styled-components"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import Typography from "components/styled/Typography"
import { emptyCarsFilter } from "redux/actions"
import useEventListener from "hooks/useEventListener"
import { sendEvent } from "scripts/GoogleAnalytics"
import Icon from "components/icons/icon"
import Trash from "components/icons/trash"
import { Filters } from "components/icons/filters"
import WhiteButton from "components/styled/Buttons/WhiteButton"
import useDidUpdate from "hooks/useDidUpdate"
import { color, mq } from "helpers/styled"
import { RentingContext } from "../RentingContext"
import Tabs from "./Tabs"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;

  @media ${mq("sm")} {
    flex-flow: row wrap;
  }

  @media ${mq("lg")} {
    margin: 0 -28px;

    &.with-filter {
      margin: 0;
    }
  }

  @media ${mq("xl")} {
    margin: 0;

    &.with-filter {
      margin: 0;
    }
  }
`

const FilterBlock = styled.div`
  width: 325px;
  max-width: 100%;
  flex: 0 0 auto;
  margin-bottom: 20px;

  @media ${mq("md")} {
    width: auto;
  }
`

const TabsBlock = styled.div`
  width: 325px;
  max-width: 100%;
  margin-bottom: 20px;

  @media ${mq("md")} {
    width: auto;
    flex: 1 1 auto;
    margin-left: 18px;
  }
`

const FilterButton = styled(WhiteButton)`
  position: relative;
  display: ${({ mobile }) => (mobile ? "flex" : "none")};
  width: 100%;
  padding-top: 9px;
  margin: 0 auto 0 0;
  padding-bottom: 9px;
  color: ${color("deepPink")};
  font-weight: 600;

  @media (min-width: 350px) {
    width: 120px;
    min-width: 120px;
  }

  @media ${mq("lg")} {
    display: ${({ mobile }) => (mobile ? "none" : "flex")};
    width: 150px;
  }
`

const FilterActions = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  @media ${mq("md")} {
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0;
  }
`

const ClearButton = styled.button`
  display: flex;
  background: none;
  border: none;
  width: 100%;
  margin-left: 25px;

  @media ${mq("xs")} {
    width: auto;
  }
`

const TrashIcon = styled(Icon)`
  margin-left: 15px;
`

const Counter = styled.span`
  background: ${color("deepPink")};
  color: white;
  font-size: 11px;
  line-height: 18px;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  position: absolute;
  right: 10px;
  top: 50%;
  margin-top: -8px;
`

const FloatingFilter = styled.div`
  position: fixed;
  top: 25px;
  left: 25px;
  z-index: 3;
  background: white;
  width: 48px;
  height: 48px;
  border-radius: 24px;
  box-shadow: 0 2px 18px 0 rgba(195, 205, 224, 0.8);
  font-size: 24px;
  text-align: center;
  line-height: 50px;

  ${Counter} {
    right: -2px;
    top: -2px;
    margin: 0;
  }
`

const isServer = typeof window === "undefined"

function Actions({ show, setShow, isDesktop, hasFilter }) {
  const { page, disableUrlFilters } = useContext(RentingContext)
  const { filterCount } = useSelector(mapStateToProps, shallowEqual)
  const t = useT()
  const dispatch = useDispatch()
  const buttonRef = useRef(null)
  const [floating, setFloating] = useState(false)

  useDidUpdate(() => {
    if (show) {
      sendEvent("Filter", { event_category: page, event_label: `Filter open up${floating ? " floating" : ""}` })
    }
  }, [show])

  const handleScroll = () => {
    if (!buttonRef.current) return
    setFloating(window.scrollY > buttonRef.current.offsetTop)
  }

  useEventListener("scroll", handleScroll, {
    enabled: !isServer,
    target: !isServer && window
  })

  const clearFilters = () => {
    sendEvent("Filter delete", { event_category: page, event_label: "delete" })
    if (disableUrlFilters && typeof window !== "undefined") {
      window.location.assign("/es/renting")
      return
    }
    dispatch(emptyCarsFilter())
  }

  return (
    <Container className={show === "desktop" ? "with-filter" : ""}>
      <FilterBlock>
        <FilterActions ref={buttonRef}>
          {hasFilter && (
            <>
              {!isDesktop && floating && (
                <FloatingFilter onClick={() => setShow(isDesktop ? "desktop" : "mobile")}>
                  <Icon Icon={Filters} color="deepPink" />
                  {filterCount > 0 && <Counter>{filterCount}</Counter>}
                </FloatingFilter>
              )}
              <FilterButton size="md" onClick={() => setShow("mobile")} mobile>
                {t("renting.filter.title")}
                {filterCount > 0 && <Counter>{filterCount}</Counter>}
              </FilterButton>
              {isDesktop && show !== "desktop" && (
                <FilterButton size="md" onClick={() => setShow("desktop")}>
                  {t("renting.filter.title")}
                  {filterCount > 0 && <Counter>{filterCount}</Counter>}
                </FilterButton>
              )}
              {show !== "desktop" && filterCount > 0 && (
                <ClearButton onClick={clearFilters}>
                  <Typography variant="text3" color="deepPink">
                    {t("renting.filter.clear")}
                  </Typography>
                  <TrashIcon Icon={Trash} color="deepPink" size="sm" />
                </ClearButton>
              )}
            </>
          )}
        </FilterActions>
      </FilterBlock>
      <TabsBlock>
        <Tabs />
      </TabsBlock>
    </Container>
  )
}

Actions.defaultProps = {
  show: null
}

Actions.propTypes = {
  show: PropTypes.string,
  setShow: PropTypes.func.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  hasFilter: PropTypes.bool.isRequired
}

const mapStateToProps = state => {
  const { carsReducer } = state
  const { filter } = carsReducer

  return { filterCount: Object.keys(filter).length }
}

export default Actions
